import { lazy } from 'react';
import type { AppManifest } from '@/apps/types';

/**
 * The file and folder property sheet.
 *
 * The one page of a property sheet anyone ever read, and until now the one
 * place in the shell where a right-click gave you a picture of a dialog
 * instead of the dialog: Properties raised a message box listing a type and a
 * path. Everything on the General page below is derived from the disk rather
 * than typed out, and `fs/stat` is where the sizes and the timestamps come
 * from, so the numbers agree with Explorer's Details columns.
 *
 * A dialog rather than a program, which is what a property sheet raised from a
 * context menu was: no taskbar button, Escape closes it, and it opens over
 * whatever asked for it. The Recycle Bin keeps its own sheet, which is a real
 * settings page and not this.
 *
 * Args:
 *   path  string  the file or folder to describe
 */
export const filePropsApp: AppManifest = {
  id: 'fileprops',
  name: 'Properties',
  icon: '/assets/icons/file.svg',
  component: lazy(() => import('./FileProps')),

  // XP's own sheet, near enough: fixed, and tall enough for the NTFS General
  // page, which is the longest of them.
  defaultSize: { w: 356, h: 456 },
  resizable: false,
  maximizable: false,
  singleton: false,
  dialog: true,
  showInAllPrograms: false,

  // The caption before the component has mounted and set its own. Written out
  // here rather than borrowed from `fs/icons`, which imports the registry this
  // manifest is about to be listed in.
  titleFor: (args) => {
    const path = typeof args.path === 'string' ? args.path : '';
    const cut = path.lastIndexOf('\\');
    const name = (cut === -1 ? path : path.slice(cut + 1)).replace(/\.(lnk|url)$/i, '');
    return name ? `${name} Properties` : 'Properties';
  },
};
